import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { DashLangService } from "../../../../../../modules/home/dash/service/dash.lang.service";
import { S25DatepickerModule } from "../../../../../../modules/s25-datepicker/s25.datepicker.module";
import { S25DropdownPaginatedModule } from "../../../../../../modules/s25-dropdown/s25.dropdown.paginated.module";
import { HelpTopic } from "../../../../../../modules/s25-help/s25.help.service";
import { TypeManagerDecorator } from "../../../../../../main/type.map.service";
import { DatetimeService } from "../../../../../../services/datetime.service";
import { StateService } from "../../../../../../services/state.service";
import { DropDownItem } from "../../../../../../pojo/DropDownItem";
import { s25Resource } from "../../../../../../util/s25.resource";
import { S25Util } from "../../../../../../util/s25-util";

/** Lang map key is legacy `find_time`; UI copy is "find availability" for a known space name. */
type FindAvailabilityLang = {
    style: {
        panel: string;
        try_browse: string;
        search: string;
        search_input: string;
    };
    text: {
        find_location_byname: string;
        button: string;
        try_browse: string;
    };
    space_availability_container: {
        tooltip_help: { content: string; helplinkId: HelpTopic; helplinkText: string };
        style: {
            panel: string;
            spaces_select_container: string;
            spaces_select: string;
            spaces_select_icon: string;
            space_date: string;
            button: string;
        };
        text: {
            select_your_location: string;
            space_date: string;
            button: string;
        };
    };
};

/** Find availability for a known space name (available-space panel path 4002). */
@TypeManagerDecorator("s25-ng-dash-available-space-find-availability")
@Component({
    selector: "s25-ng-dash-available-space-find-availability",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25DatepickerModule, S25DropdownPaginatedModule],
    host: { class: "s25-ng-dash-available-space-find-availability" },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        label {
            font-weight: bold;
        }

        #My25Live_EventCreation_SomewhereBetween {
            margin-top: 0;
        }

        s25-ng-dropdown-search-criteria {
            display: block;
        }

        s25-datepicker {
            align-self: flex-start;
        }
    `,
    template: `
        @if (findAvailabilityLang.value(); as context) {
            @if (availabilityLang(); as availability) {
                <label>{{ context.text.find_location_byname }}</label>
                <s25-ng-dropdown-search-criteria
                    class="c-input"
                    [attr.id]="availability.style.spaces_select"
                    [type]="'locations'"
                    [customFilterValue]="schedulableLocationsFilter"
                    [placeholder]="availability.text.select_your_location"
                    [value]="selectedLocation()"
                    (valueChange)="onLocationChange($event)"
                />

                @if (selectedLocation()?.itemId) {
                    <label [attr.id]="availability.style.space_date">{{ availability.text.space_date }}</label>
                    <s25-datepicker
                        [value]="selectedDate()"
                        (valueChange)="selectedDate.set($event)"
                        [showToday]="true"
                    />

                    <button type="button" [class]="availability.style.button" (click)="submitAvailabilitySearch()">
                        {{ availability.text.button }}
                    </button>
                }
            }

            <a
                tabindex="0"
                class="text-main"
                [attr.id]="context.style.try_browse"
                (click)="browseLocationGroups()"
                (keydown.enter)="browseLocationGroups()"
            >
                {{ context.text.try_browse }}
            </a>
        }
    `,
})
export class HomeDashAvailableSpaceFindAvailabilityComponent {
    /** Matches legacy {@link SpaceService.getSpacesOptions} schedulable-only filter. */
    readonly schedulableLocationsFilter = "&can_schedule=T";

    readonly findAvailabilityLang = s25Resource(
        async () => (await DashLangService.getLang()).panel?.available_space?.find_time as FindAvailabilityLang,
    );

    readonly selectedLocation = signal<DropDownItem | undefined>(undefined);
    readonly selectedDate = signal<Date | undefined>(undefined);

    readonly availabilityLang = computed(() => this.findAvailabilityLang.value()?.space_availability_container);

    constructor() {
        this.ensureTodaySelected();
    }

    browseLocationGroups() {
        void StateService.gotoItem({ itemTypeId: 204 });
    }

    onLocationChange(location: DropDownItem | undefined) {
        this.selectedLocation.set(location);
        if (location?.itemId) {
            this.ensureTodaySelected();
        }
    }

    private ensureTodaySelected() {
        if (!S25Util.isUndefined(this.selectedDate())) {
            return;
        }
        void DatetimeService.currentDay().then((today) => this.selectedDate.set(today));
    }

    submitAvailabilitySearch() {
        const itemId = this.selectedLocation()?.itemId;
        if (S25Util.isUndefined(itemId) || !Number.isFinite(Number(itemId))) {
            return;
        }
        void StateService.gotoItem({
            itemTypeId: 4,
            itemId: Number(itemId),
            startDt: this.selectedDate(),
            forceView: "availability",
        } as Parameters<typeof StateService.gotoItem>[0] & { startDt?: Date });
    }
}
